export const onboardingEn = {
  title: "Quick setup",
  subtitle: "A couple of choices to personalize Floline.",
  step: "Step {{current}} of {{total}}",
  actions: {
    back: "Back",
    next: "Next",
    skip: "Skip for now",
    finish: "Finish setup",
  },
  language: {
    title: "Language",
    description: "Choose the language used across the app.",
    label: "Application language",
    english: "English",
    finnish: "Suomi",
  },
  name: {
    title: "Your name",
    description:
      "We use your name for greetings and exports. You can change it later in Settings.",
    firstName: "First name",
    lastName: "Last name",
    required: "Please enter at least a first name.",
  },
  paperdoll: {
    title: "Paper doll",
    description:
      "Pick the body diagram style used when you log pain locations. This only affects visuals.",
    label: "Body model",
    masculine: "Masculine",
    feminine: "Feminine",
  },
  location: {
    title: "Location (optional)",
    description:
      "If you set a location, Trends can show daily temperature context and weather-adjusted insights.",
    privacyNote:
      "We store your selected city and its coordinates for weather and timezone context. We do not track your device GPS or movements. You can remove it anytime.",
    searchLabel: "City search",
    searchPlaceholder: "Start typing a city (e.g. Helsinki)",
    searchButton: "Add",
    searchHint: "Select a city from the suggestion list.",
    searching: "Searching cities...",
    noMatches: "No city matches that search.",
    selectionRequired: "Pick a city from the suggestion list before adding.",
    noLocations: "No locations saved yet.",
    defaultBadge: "Default",
    setDefault: "Set default",
    deleteLocation: "Remove",
    setDefaultFailed: "Couldn't set default location. Try again.",
    deleteFailed: "Couldn't remove location. Try again.",
    rateLimitError: "Too many requests. Please wait a moment and try again.",
    addFailed: "Couldn't add that location. Try a different city name.",
    temperatureToggle: "Show temperature context in Trends",
    temperatureHint:
      "Turns on the temperature overlay in Trends. You can change this in Settings later.",
  },
  consent: {
    title: "Data & privacy consents",
    description:
      "Floline processes sensitive health data. Please review each consent below before continuing.",
    saveError: "Could not save consent choices. Please try again.",
    coreHealthProcessing: {
      label: "Core health-data processing (required)",
      text: "I consent to Floline storing and processing the pain, symptom, and well-being data I enter so the app can display my health journal, charts, and trends.",
      required: "You must accept core health-data processing to use Floline.",
    },
    audioTranscription: {
      label: "Audio transcription",
      text: "I consent to my voice recordings being sent to a speech-to-text service to transcribe check-in entries. Recordings are deleted after transcription.",
    },
    aiExtraction: {
      label: "AI-assisted data extraction",
      text: "I consent to my check-in text being sent to an AI model to extract structured pain and symptom data. No data is used for model training.",
    },
    aiInsights: {
      label: "AI-generated insights",
      text: "I consent to my aggregated health data being analysed by an AI model to generate personalised trends, summaries, and suggestions.",
    },
  },
  finish: {
    title: "You're set",
    description: "You can revisit any of these in Settings whenever you want.",
  },
} as const;

export const onboardingFi = {
  title: "Pika-asetukset",
  subtitle: "Muutama valinta, joilla personoit Flolinen.",
  step: "Vaihe {{current}} / {{total}}",
  actions: {
    back: "Takaisin",
    next: "Seuraava",
    skip: "Ohita toistaiseksi",
    finish: "Valmis",
  },
  language: {
    title: "Kieli",
    description: "Valitse sovelluksessa käytettävä kieli.",
    label: "Sovelluksen kieli",
    english: "English",
    finnish: "Suomi",
  },
  name: {
    title: "Nimesi",
    description:
      "Käytämme nimeä tervehdyksissä ja viennissä. Voit muuttaa sitä myöhemmin Asetuksissa.",
    firstName: "Etunimi",
    lastName: "Sukunimi",
    required: "Syötä vähintään etunimi.",
  },
  paperdoll: {
    title: "Paperinukke",
    description:
      "Valitse kehokartan tyyli, jota käytetään kipukohtien kirjaamisessa. Tämä vaikuttaa vain ulkoasuun.",
    label: "Kehomalli",
    masculine: "Maskuliininen",
    feminine: "Feminiininen",
  },
  location: {
    title: "Sijainti (valinnainen)",
    description:
      "Kun asetat sijainnin, Trendit voi näyttää päivittäisen lämpötilakontekstin ja sääpohjaisia havaintoja.",
    privacyNote:
      "Tallennamme valitsemasi kaupungin ja sen koordinaatit sää- ja aikavyöhykekontekstia varten. Emme seuraa laitteesi GPS-sijaintia tai liikkumista. Voit poistaa sen milloin tahansa.",
    searchLabel: "Kaupunki",
    searchPlaceholder: "Kirjoita kaupungin nimi (esim. Helsinki)",
    searchButton: "Lisää",
    searchHint: "Valitse kaupunki ehdotuslistasta.",
    searching: "Haetaan kaupunkeja...",
    noMatches: "Hakua vastaavaa kaupunkia ei löytynyt.",
    selectionRequired: "Valitse kaupunki ehdotuslistasta ennen lisäämistä.",
    noLocations: "Ei tallennettuja sijainteja.",
    defaultBadge: "Oletus",
    setDefault: "Aseta oletukseksi",
    deleteLocation: "Poista",
    setDefaultFailed: "Oletussijainnin asetus epäonnistui. Yritä uudelleen.",
    deleteFailed: "Sijainnin poisto epäonnistui. Yritä uudelleen.",
    rateLimitError: "Liian monta pyyntöä. Odota hetki ja yritä uudelleen.",
    addFailed: "Sijaintia ei voitu lisätä. Kokeile toista kaupungin nimeä.",
    temperatureToggle: "Näytä lämpötilakonteksti Trendeissä",
    temperatureHint:
      "Kytkee lämpötilakerroksen päälle Trendeissä. Voit muuttaa tätä myöhemmin Asetuksissa.",
  },
  consent: {
    title: "Tietojen käsittely ja suostumukset",
    description:
      "Floline käsittelee arkaluonteista terveystietoa. Tarkista alla olevat suostumukset ennen jatkamista.",
    saveError: "Suostumusten tallentaminen epäonnistui. Yritä uudelleen.",
    coreHealthProcessing: {
      label: "Terveystietojen peruskäsittely (pakollinen)",
      text: "Annan suostumukseni siihen, että Floline tallentaa ja käsittelee syöttämäni kipu-, oire- ja hyvinvointitiedot, jotta sovellus voi näyttää terveyspäiväkirjani, kaaviot ja trendit.",
      required: "Sinun on hyväksyttävä terveystietojen peruskäsittely käyttääksesi Flolinea.",
    },
    audioTranscription: {
      label: "Äänen litterointi",
      text: "Annan suostumukseni siihen, että äänitallenteeni lähetetään puheentunnistuspalveluun kirjausten litteroimiseksi. Tallenteet poistetaan litteroinnin jälkeen.",
    },
    aiExtraction: {
      label: "Tekoälypohjainen tietojen poiminta",
      text: "Annan suostumukseni siihen, että kirjaustekstini lähetetään tekoälymallille jäsennellyn kipu- ja oiretiedon poimimiseksi. Tietoja ei käytetä mallin kouluttamiseen.",
    },
    aiInsights: {
      label: "Tekoälyn tuottamat oivallukset",
      text: "Annan suostumukseni siihen, että koostettua terveystietoani analysoidaan tekoälymallilla henkilökohtaisten trendien, yhteenvetojen ja ehdotusten tuottamiseksi.",
    },
  },
  finish: {
    title: "Valmis",
    description: "Voit palata näihin milloin tahansa Asetuksissa.",
  },
} as const;
